import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CaseData, CaseStatus } from "@kahunmedical/common-wtypes";
import { isRtl } from "@kahunmedical/common-configuration";
import { localeSettings } from "../clinicSettings/localeDefaults";
import { useClinicState } from "../../../clinic/clinicStateContext";
import { usePatientData } from "../../../clinic/hooks/usePatientData";
import {
  Badge,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  styled,
  Tooltip,
} from "@mui/material";
import EnhancedTable, {
  EnhancedTableColumn,
  ExpandClickableAreas,
} from "fe-common/ui/table/enhancedTable";
import { PatientStatus } from "fe-web-components/patientCard/patientStatus";
import { format } from "date-fns";
import { PatientCard } from "fe-web-components/patientCard/patientCard";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { isProduction } from "fe-common/utils/envGetter";
import { FilterInput, Order, SortType } from "fe-common/graphql";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { LoadingProgress } from "fe-web-components/loader/loadingProgress";
import { PrivatePlaceholder } from "fe-web-components/patientCard/PrivatePlaceholder";
import { Panels } from "shared";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ClinicPageWrapper } from "../elements/clinicPageWrapper";
import { SearchInput } from "../elements/searchInput";
import { ClinicDialog } from "../elements/clinicDialog";
import { PatientsTableFilter } from "./patientsTableFilter";
import { useParams } from "react-router";
import { useDiagnosticNavigation } from "../../diagnosticAgent/useDiagnosticNavigation";
import { openInEditor } from "fe-common/utils/clinicUtils";
import { getFeatureIcon } from "fe-common/iconManager/chckIfFeatureHasIcons";
import { shorten } from "fe-common/utils/shorterText";
import { PatientCardNav } from "./patientCardNav";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { useClinicUserContext } from "../clinicUserContext";
import { ReactComponent as FilterIcon } from "../../../assets/clinic/filterIcon.svg";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import MessageIcon from "@mui/icons-material/Message";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import ShopTwoOutlinedIcon from "@mui/icons-material/ShopTwoOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import {
  useClinicWalkthrough,
  WalkthroughSections,
} from "../../../clinic/clinicWalkthroughContext";
import { PatientTableCopyCase } from "./patientTableCopyCase";
import { useClinicMessagesContext } from "../clinicMessagesContext";
//region [[ Styles ]]
const ClinicPatientsView = styled(ClinicPageWrapper)({
  "& .error": {
    fontWeight: 700,
    fontSize: "12px",
    color: "maroon",
  },
  "& .enhanced-active-row > td": {
    fontWeight: 500,
  },
  "& .enhanced-table-expanded > td, & .enhanced-table-expanded-preview > td": {
    height: 0, //hack for 100% height inner flex box
    padding: "3px 0",
  },
});

const ReasonForVisitHolder = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
  "& .reason-icon": {
    width: 24,
  },
  "& .reason-comment": {
    display: "inline-block",
    verticalAlign: "middle",
  },
});

const FilterBadge = styled(Badge)({
  "& .MuiBadge-badge": {
    right: 5,
    top: 5,
  },
});

const StyledToolTip = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  alignItems: "start",
  "& .comment-type": {
    fontWeight: "bold",
  },
});

const BackLink = styled(Button)({
  fontSize: 14,
  lineHeight: 1,
  textTransform: "none",
});

const HeaderButtons = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginLeft: "auto",
  "& .header-button": {
    fontSize: 12,
    minWidth: 20,
  },
});

const AddCaseButton = styled(ClinicActionButton)({
  "& .MuiButton-startIcon": {
    marginLeft: -8,
    marginRight: 2,
  },
});

//endregion [[ Styles ]]
//region [[ Props ]]

export interface PatientFilterParams {
  fromDate?: Date;
  toDate?: Date;
  statuses?: CaseStatus[];
  finished?: boolean;
  today?: boolean;
}

interface SortByProps {
  column: string;
  sortKey: string;
  order: Order;
}

const defaultFilterParams: PatientFilterParams = {
  fromDate: null,
  toDate: null,
  statuses: [CaseStatus.COMPLETED, CaseStatus.ABANDONED],
  finished: true,
  today: false,
};

const RECORDS_LIMIT_DEFAULT = 250;

type LastOpenedMetaData = {
  id: string;
  timestamp: number;
};

//endregion [[ Props ]]
//region [[ Functions ]]
export const onViewOnAgent = (
  caseId: string,
  clinicId: string,
  clinicName: string,
  clinicMode: boolean = true,
) => {
  evt({
    category: CATEGORY.CLINIC_MANAGEMENT,
    action: ACTION.CLINIC_OPEN_PATIENT_IN_AGENT,
    props: {
      clinicName,
      patientId: caseId,
    },
  });
  const url = `${
    isProduction()
      ? "https://diagnose.kahun.com/"
      : "https://develop.kahun.com/"
  }${clinicMode ? "clinic/" : ""}agent/dd?shareId=${caseId}&sort_type=${
    SortType.PatientProb
  }&dd_panel=${Panels.PatientInternalMedicine}&clinic=${clinicId}`;
  window.open(url, "_blank");
};

//endregion [[ Functions ]]

export const PatientsTable = ({
  onShowNewCase,
}: {
  onShowNewCase: () => void;
}) => {
  const [
    {
      clinicData: { id: clinicId, name: clinicName, displayName },
      defaultLocale,
      isDemoClinic,
      clinicConfig,
      gotoCase,
      privateMode,
    },
  ] = useClinicState();
  const { setMessage } = useClinicMessagesContext();
  const rtlDisplay = isRtl(clinicConfig.defaultLocale);

  const { setSection, start: startWalkthrough } = useClinicWalkthrough();

  const isEqual = require("react-fast-compare");
  const { hasAdminAccess, hasSuperAccess, username } = useClinicUserContext();
  const { translate } = useTranslateByGender();

  const tableColumns = useMemo<EnhancedTableColumn[]>(() => {
    return [
      {
        id: "creationDateTime",
        name: "Created at",
        enableSort: true,
        onSort: () => {
          handleSort("creationDateTime", "date");
        },
        customSort: () => {
          return 0; //use default sorting from server
        },
        render: (rowData: CaseData) => {
          return (
            rowData.date &&
            format(new Date(rowData.date), "Pp", {
              locale: (localeSettings[defaultLocale] || localeSettings["en"])
                ?.localeFormat,
            })
          );
        },
        hidden: !clinicConfig?.standalone.showCreationDate,
      },
      {
        id: "fullName",
        name: "Full Name",
        enableSort: false,
        onSort: () => {
          handleSort("fullName", "first_name");
        },
        customSort: () => {
          return 0; //use default sorting from server
        },
        render: (rowData: CaseData) =>
          privateMode && rowData.patient?.displayName ? (
            <PrivatePlaceholder />
          ) : (
            <strong>{rowData.patient.displayName}</strong>
          ),
        hidden: !clinicConfig?.standalone.showPatientName,
      },
      {
        id: "visitReason",
        name: "Reason for Visit",
        enableSort: false,
        render: (rowData: CaseData) => {
          const { comment } = rowData;
          return (
            <ReasonForVisitHolder>
              {rowData.visitReason && (
                <>
                  <img
                    alt={rowData.visitReason?.name}
                    src={getFeatureIcon(rowData.visitReason?.id)}
                    className="reason-icon"
                  />
                  <span>{rowData.visitReason?.name}</span>
                </>
              )}
              {comment && (
                <Tooltip
                  title={
                    <StyledToolTip
                      className={`bigger-tooltip multiline-tooltip${
                        rtlDisplay ? " rtl-tooltip" : ""
                      }`}
                    >
                      {comment && (
                        <div>
                          <div className="comment-type">
                            {translate("PHYSICIAN_NOTES")}
                          </div>
                          {comment}
                        </div>
                      )}
                    </StyledToolTip>
                  }
                  arrow={true}
                  className={"bigger-tooltip"}
                  placement="top"
                >
                  <MessageIcon
                    fontSize="small"
                    color="secondary"
                    className="reason-comment"
                  />
                </Tooltip>
              )}
            </ReasonForVisitHolder>
          );
        },
      },
      {
        id: "age",
        name: "Age",
        enableSort: false,
        render: (rowData: CaseData) => rowData.patient.age,
      },
      {
        id: "gender",
        name: "Gender",
        enableSort: false,
        render: (rowData: CaseData) => (
          <span style={{ textTransform: "capitalize" }}>
            {rowData.patient.gender}
          </span>
        ),
      },
      {
        id: "chiefComplaint",
        name: "Chief Complaint",
        enableSort: false,
        render: (rowData: CaseData) => {
          const complaintText =
            rowData.chiefComplaint?.shortname || rowData.encounterText || "";
          return (
            <div dir={rtlDisplay ? "rtl" : "ltr"}>
              {complaintText.length < 41 ? (
                complaintText
              ) : (
                <Tooltip
                  title={
                    <StyledToolTip
                      className={`bigger-tooltip multiline-tooltip${
                        rtlDisplay ? " rtl-tooltip" : ""
                      }`}
                    >
                      <div>
                        <div className="comment-type">
                          {translate("PATIENT_REQUEST")}
                        </div>
                        {complaintText}
                      </div>
                    </StyledToolTip>
                  }
                  arrow={true}
                  className={"bigger-tooltip"}
                  placement="top"
                >
                  <span>{shorten(complaintText, 40)}</span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        id: "patientCode",
        name: "Patient Code",
        enableSort: false,
        render: (rowData: CaseData) => rowData.digitCode,
      },
      {
        id: "visitDateTime",
        name: "Time of Visit",
        enableSort: true,
        onSort: () => {
          handleSort("visitDateTime", "visit_date");
        },
        customSort: () => {
          return 0; //use default sorting from server
        },
        render: (rowData: CaseData) => {
          return rowData.visitDate
            ? format(new Date(rowData.visitDate), "Pp", {
                locale: localeSettings[defaultLocale].localeFormat,
              })
            : "";
        },
        hidden: !clinicConfig?.standalone.showVisitDate,
      },
      {
        id: "status",
        name: "Status",
        enableSort: false,
        render: (rowData: CaseData) => (
          <PatientStatus
            status={rowData.status}
            updated={format(new Date(rowData.date), "Pp", {
              locale: (localeSettings[defaultLocale] ?? localeSettings["en"])
                ?.localeFormat,
            })}
          />
        ),
      },
      // {
      //   id: "redFlag",
      //   name: "Ref Flag",
      //   enableSort: true,
      // },
    ];
  }, [privateMode, clinicConfig, defaultLocale, rtlDisplay, translate]);

  const { code: digitCode } = useParams<{ code: string }>();

  const [lastOpened, setLastOpened] = useState<LastOpenedMetaData>(null); //used for saving patient expand event duration
  const [searchTerm, setSearchTerm] = useState<string>(
    sessionStorage.getItem("searchTerm" + clinicId) || "",
  ); //used for debouncing search

  const [tableData, setTableData] = useState<CaseData[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<CaseData>(null);
  const [confirmDeletePatient, setConfirmDeletePatient] = useState<CaseData>(
    null,
  );
  const [showCopyCaseToClinicDialog, setShowCopyCaseToClinicDialog] = useState<
    boolean
  >(false);

  const [deletingPatients, setDeletingPatients] = useState<string[]>([]);
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [expandedPatient, setExpandedPatient] = useState<{
    index: number;
    data: CaseData;
  }>(null);
  const [sortBy, setSortBy] = useState<SortByProps>({
    column: "visitDateTime",
    sortKey: "visit_date",
    order: Order.Desc,
  });
  const [filter, setFilter] = useState<FilterInput>({});
  const [filterParams, setFilterParams] = useState<PatientFilterParams>(
    sessionStorage.getItem("filterParams" + clinicId)
      ? JSON.parse(sessionStorage.getItem("filterParams" + clinicId))
      : defaultFilterParams,
  );
  const [highlightedRow, setHighlightedRow] = useState(-1);
  const eventRef = useRef<ACTION>(null);
  const resultsRef = useRef(-1);

  const handlePatientMenuOpen = (
    event,
    patientData: CaseData,
    rowIndex?: number,
  ) => {
    setSelectedPatient(patientData);
    setMenuAnchor(event.currentTarget);
    if (rowIndex > -1) setHighlightedRow(rowIndex);
  };

  const handlePatientMenuClose = () => {
    setMenuAnchor(null);
    setHighlightedRow(-1);
  };

  const handleSort = (column: string, sortKey: string) => {
    setSortBy((prevState) => ({
      column,
      sortKey,
      order:
        prevState.column === column && prevState.order === Order.Desc
          ? Order.Asc
          : Order.Desc,
    }));
  };

  const noFilter = useMemo<boolean>(
    () => JSON.stringify(filterParams) === JSON.stringify(defaultFilterParams),
    [filterParams],
  );

  const {
    loadingCasesList,
    loadingCaseData,
    loadedClinic,
    casesList,
    deleteCode,
    addComment,
    onLoadMore,
    hasMore,
    moreLoading,
    totalCases,
  } = usePatientData({
    clinicId,
    limit: RECORDS_LIMIT_DEFAULT,
    sortBy: sortBy?.sortKey,
    order: sortBy?.order,
    queryFilter: filter,
    finished: filterParams.finished,
    digitCode,
  });

  const { setFeedbackDialogProps } = useDiagnosticNavigation();

  const deleteItem = (userCodeId: string) => {
    hideCaseData();
    setConfirmDeletePatient(null);
    setSelectedPatient(null);
    setDeletingPatients((prevState) => [...prevState, userCodeId]);
    deleteCode(userCodeId).then(() =>
      setDeletingPatients((prevState) =>
        prevState.filter((d) => d !== userCodeId),
      ),
    );
  };

  const copyCaseId = () => {
    handlePatientMenuClose();
    const caseId = selectedPatient.caseId;
    navigator.clipboard.writeText(caseId).then(() =>
      setMessage({
        text: (
          <>
            Case ID copied to clipboard: <strong>{caseId}</strong>
          </>
        ),
      }),
    );
  };

  const openInExplorer = () => {
    handlePatientMenuClose();
    openInEditor("wconversationcase/" + selectedPatient.caseId);
  };

  const openCopyInAgent = () => {
    handlePatientMenuClose();
    onViewOnAgent(selectedPatient.caseCode, clinicId, clinicName, false);
  };

  const openFeedBack = () => {
    handlePatientMenuClose();
    setFeedbackDialogProps({
      open: true,
      caseId: selectedPatient.caseCode,
      clinicName: displayName,
    });
  };

  const showCaseData = useCallback(
    (source: ExpandClickableAreas, digitCode?: string, caseIndex?: number) => {
      if (digitCode) {
        //search selected case data in existing table
        caseIndex = tableData?.findIndex((c) => c.digitCode === digitCode);
        if (caseIndex < 0) {
          //wait for relevant cases to be loaded and try again
          console.log("Waiting for case code", digitCode);
          return null;
        }
      }

      const activeCaseData = { ...tableData[caseIndex] };

      setExpandedPatient({
        index: caseIndex,
        data: activeCaseData,
      });

      const timestamp = Date.now();
      if (lastOpened) {
        const reviewDuration = `${(timestamp - lastOpened.timestamp) / 1000}s`;
        // card was closed
        evt({
          category: CATEGORY.CLINIC_MANAGEMENT,
          action: ACTION.CLINIC_CLOSED_PATIENT_CARD,
          props: {
            clinicName,
            reviewDuration,
          },
        });
        setLastOpened(null);
      } else {
        // Expanded
        evt({
          category: CATEGORY.CLINIC_MANAGEMENT,
          action: ACTION.CLINIC_OPEN_PATIENT_CARD,
          props: {
            timestamp,
            clinicName,
            openingMethod: source,
            patientId: activeCaseData.caseCode,
          },
        });
        setLastOpened({
          id: activeCaseData.caseCode,
          timestamp: timestamp,
        });
      }

      gotoCase(activeCaseData.digitCode); //update url
    },
    [tableData, clinicName, lastOpened, gotoCase],
  );

  const hideCaseData = () => {
    gotoCase(null);
    setExpandedPatient(null);
  };

  useEffect(() => {
    //open patient card according to url
    if (digitCode) {
      if (expandedPatient?.data.digitCode !== digitCode)
        showCaseData(ExpandClickableAreas.NavigateTo, digitCode);
    } else setExpandedPatient(null);
  }, [showCaseData, expandedPatient, digitCode]);

  useEffect(() => {
    if (setSection)
      setSection(
        expandedPatient
          ? WalkthroughSections.PATIENT_CARD
          : WalkthroughSections.WELCOME,
      );
  }, [expandedPatient, setSection]);

  useEffect(() => {
    const updatedFilter: FilterInput = {};
    if (isEqual(filterParams, defaultFilterParams)) {
      sessionStorage.removeItem("filterParams" + clinicId);
    } else {
      sessionStorage.setItem(
        "filterParams" + clinicId,
        JSON.stringify(filterParams),
      );
    }
    if (filterParams.today) {
      const today = new Date();
      updatedFilter.toVisitDate = today.setHours(23, 59, 59, 999);
      updatedFilter.patientProperties = {
        visit_date: today.setHours(0, 0, 0, 0),
      };
    } else {
      if (filterParams.toDate)
        updatedFilter.toVisitDate = new Date(filterParams.toDate).setHours(
          23,
          59,
          59,
          999,
        );
      if (filterParams.fromDate)
        updatedFilter.patientProperties = {
          visit_date: new Date(filterParams.fromDate).setHours(0, 0, 0, 0),
        };
    }
    if (searchTerm) {
      sessionStorage.setItem("searchTerm" + clinicId, searchTerm);
      if (isNaN(parseInt(searchTerm)))
        //full name search
        updatedFilter.fullNameFilter = searchTerm;
      else updatedFilter.code = searchTerm; // code search
    } else {
      sessionStorage.removeItem("searchTerm" + clinicId);
    }
    setFilter(updatedFilter);
  }, [clinicId, searchTerm, filterParams, isEqual]);

  useEffect(() => {
    if (casesList && casesList.length) {
      setTableData(casesList);
    } else {
      setTableData([]);
    }
    resultsRef.current = casesList?.length || 0;
  }, [casesList]);

  useEffect(() => {
    if (resultsRef.current > -1 && eventRef.current) {
      evt({
        category: CATEGORY.CLINIC_MANAGEMENT,
        action: eventRef.current,
        props: {
          resultCount: resultsRef.current,
          filter: { ...filter, started: !filterParams.finished },
          searchTerm: searchTerm,
          clinicName: displayName,
          userName: username,
        },
      });
      eventRef.current = null;
    }
  }, [displayName, searchTerm, filterParams, filter, username]);

  return (
    <ClinicPatientsView
      pageTitle={
        <>
          {expandedPatient && (
            <BackLink
              color="inherit"
              onClick={hideCaseData}
              startIcon={<ArrowBackIcon />}
            >
              {!isDemoClinic || hasSuperAccess
                ? "Back to dashboard"
                : "All cases"}
            </BackLink>
          )}
          {(!isDemoClinic || hasSuperAccess) && (
            <SearchInput
              onSearch={(searchTerm) => {
                eventRef.current = ACTION.CLINIC_SEARCHED;
                resultsRef.current = -1; //wait for query results before firing event
                setSearchTerm(searchTerm);
                hideCaseData();
              }}
              value={searchTerm}
              searchOnChange={false}
              onClear={() => {
                setSearchTerm("");
                hideCaseData();
              }}
              placeHolder="Search for name or code"
            />
          )}
          <HeaderButtons>
            {expandedPatient && (
              <PatientCardNav
                patientIndex={expandedPatient.index}
                totalCount={totalCases}
                onGotoIndex={(index: number) => {
                  showCaseData(ExpandClickableAreas.TopNav, null, index);
                }}
                loading={!loadedClinic}
              />
            )}
            {clinicConfig?.standalone.manualCaseFromSummery &&
              clinicConfig?.[ClinicConfigKeys.UsesLLM] &&
              (!expandedPatient || isDemoClinic) && (
                <AddCaseButton
                  onClick={onShowNewCase}
                  variant="outlined"
                  lightMode
                  className="header-button new-case-button"
                  label="NEW CASE"
                  icon={<AddIcon />}
                />
              )}

            {(!isDemoClinic || hasSuperAccess) && (
              <FilterBadge
                color="primary"
                variant="dot"
                invisible={noFilter}
                overlap="rectangular"
              >
                <ClinicActionButton
                  onClick={() => setShowFilterDialog(true)}
                  variant="outlined"
                  label="FILTER"
                  icon={<FilterIcon />}
                  lightMode
                  className="header-button filter-button"
                  disabled={loadedClinic !== clinicId}
                />
              </FilterBadge>
            )}
          </HeaderButtons>
        </>
      }
    >
      {//show loading message only for the first time for each clinic
      expandedPatient
        ? loadingCaseData && <LoadingProgress label={"Loading case..."} />
        : loadedClinic !== clinicId &&
          loadingCasesList && <LoadingProgress label="Loading patients..." />}

      {expandedPatient && !loadingCaseData && (
        <PatientCard
          data={expandedPatient.data}
          onSaveComment={(digitCode, comment) => {
            setExpandedPatient((prevState) => ({
              ...prevState,
              data: { ...prevState.data, comment },
            }));
            return addComment(digitCode, comment);
          }}
          onClose={hideCaseData}
          columns={tableColumns}
          onPatientMenuOpen={handlePatientMenuOpen}
          startWalkthrough={
            () => setTimeout(startWalkthrough, 600) //wait for card to render
          }
        />
      )}
      {loadedClinic === clinicId && (
        <div
          style={
            expandedPatient && {
              visibility: "hidden",
              position: "absolute",
              top: 0,
            }
          }
        >
          <EnhancedTable
            columns={tableColumns}
            data={tableData}
            expandedIndices={null}
            onRowExpanded={(source: ExpandClickableAreas, rowData: CaseData) =>
              showCaseData(source, rowData.digitCode)
            }
            sortBy={sortBy}
            floatingActionButtons={true}
            noResultsMessage={
              <span>
                {searchTerm || digitCode ? "Patient not found." : "No results"}
                <br />
                Try changing the{" "}
                <Link onClick={() => setShowFilterDialog(true)}>filter</Link>
                {searchTerm && " and search again."}
              </span>
            }
            hideToolbar
            hideChecked
            hidePagination
            fixedHeader
            maxHeight={"calc(100vh - 150px)"}
            highlightedRows={[highlightedRow]}
            hasMore={hasMore}
            loadingMore={moreLoading}
            onLoadMore={onLoadMore}
            infiniteScroll={true}
            expandedRowIndicator={false}
            rowActions={(rowData, rowIndex) => (
              <IconButton
                size={"small"}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePatientMenuOpen(e, rowData, rowIndex);
                }}
              >
                <MoreHorizIcon />
              </IconButton>
            )}
            deletingRows={deletingPatients.map((code) =>
              tableData.findIndex((c) => c.caseCode === code),
            )}
          />
        </div>
      )}

      {showCopyCaseToClinicDialog && (
        <PatientTableCopyCase
          srcClinicId={clinicId}
          caseId={selectedPatient.caseId}
          onSave={() =>
            setMessage({ text: "Case copied", severity: "success" })
          }
          onClose={() => {
            setShowCopyCaseToClinicDialog(false);
          }}
        />
      )}
      {confirmDeletePatient && (
        <ClinicDialog
          open={true}
          onClose={() => setConfirmDeletePatient(null)}
          closeButton={false}
        >
          <DialogContent style={{ padding: 8 }}>
            Are you sure you want to delete{" "}
            <strong>
              {confirmDeletePatient.patient.displayName
                ? confirmDeletePatient.patient.displayName + " - "
                : ""}
              patient {confirmDeletePatient.digitCode}?
            </strong>
          </DialogContent>
          <DialogActions>
            <ClinicActionButton
              lightMode
              variant={"outlined"}
              label="CANCEL"
              onClick={() => setConfirmDeletePatient(null)}
            />
            <ClinicActionButton
              label="DELETE"
              onClick={() => deleteItem(confirmDeletePatient.caseCode)}
            />
          </DialogActions>
        </ClinicDialog>
      )}

      <Menu
        open={menuAnchor !== null}
        id="patientMenu"
        anchorEl={menuAnchor}
        onClose={handlePatientMenuClose}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={() => {
            handlePatientMenuClose();
            setConfirmDeletePatient(selectedPatient);
          }}
        >
          <ListItemIcon>
            <DeleteForeverIcon fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
        {hasAdminAccess && (
          <MenuItem onClick={copyCaseId}>
            <ListItemIcon>
              <ShopTwoOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Copy Case ID
          </MenuItem>
        )}
        {hasAdminAccess && (
          <MenuItem onClick={openInExplorer}>
            <ListItemIcon>
              <ExploreOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Open in Explorer
          </MenuItem>
        )}
        {hasAdminAccess && (
          <MenuItem onClick={openCopyInAgent}>
            <ListItemIcon>
              <FileCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Create Copy in Agent
          </MenuItem>
        )}
        {hasSuperAccess && (
          <MenuItem onClick={() => setShowCopyCaseToClinicDialog(true)}>
            <ListItemIcon>
              <FileCopyOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Copy to another clinic
          </MenuItem>
        )}
        <MenuItem onClick={openFeedBack}>
          <ListItemIcon>
            <FeedbackOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Send Feedback
        </MenuItem>
      </Menu>

      {showFilterDialog && (
        <PatientsTableFilter
          onClose={() => setShowFilterDialog(false)}
          onFilter={(newParams) => {
            eventRef.current = ACTION.CLINIC_FILTERED;
            resultsRef.current = -1; //wait for query results before firing event
            hideCaseData();
            setFilterParams(newParams);
          }}
          locale={defaultLocale}
          filterParams={filterParams}
          defaultFilter={defaultFilterParams}
        />
      )}
    </ClinicPatientsView>
  );
};
